import { Link } from '@tanstack/react-router';
import type { System } from '@/types/api';

/**
 * SystemName - einheitliche Darstellung eines System-Namens.
 *
 * Primär wird der `display_name` gezeigt (Fallback: `hostname`). Der `hostname`
 * erscheint nur dann zusätzlich als grauer Sekundär-Hinweis, wenn er sich vom
 * `display_name` UNTERSCHEIDET. Beim ods-Import wurde bei vielen Systemen
 * `display_name == hostname` gesetzt - ohne diese Prüfung stünde derselbe
 * Hostname doppelt (blau als Link + grau daneben) da (reine Redundanz).
 */

type SystemLike = Pick<System, 'id' | 'hostname' | 'display_name'>;

/** Primärer Anzeigename: display_name, sonst hostname. */
export function systemPrimaryName(system: Pick<System, 'hostname' | 'display_name'>): string {
  return system.display_name ?? system.hostname;
}

/** true, wenn der hostname zusätzlich (als Sekundär-Info) angezeigt werden soll. */
export function systemHasDistinctHostname(
  system: Pick<System, 'hostname' | 'display_name'>
): boolean {
  return Boolean(system.display_name && system.display_name !== system.hostname);
}

interface SystemNameProps {
  system: SystemLike;
  /** Primären Namen als Link auf das System-Detail rendern. */
  asLink?: boolean;
  /** Zusatzklassen für den Primaer-Text/-Link. */
  className?: string;
  /** Optionaler Tooltip für den Primär-Link (z.B. Scope-Hinweis). */
  title?: string;
}

export function SystemName({
  system,
  asLink = false,
  className,
  title,
}: SystemNameProps): JSX.Element {
  const primary = systemPrimaryName(system);
  const secondary = systemHasDistinctHostname(system) ? (
    <span className="ml-2 font-mono text-xs text-muted">{system.hostname}</span>
  ) : null;

  if (!asLink) {
    return (
      <>
        <span className={['font-medium', className ?? ''].join(' ')}>{primary}</span>
        {secondary}
      </>
    );
  }

  const linkProps: { title?: string } = {};
  if (title) linkProps.title = title;

  return (
    <>
      <Link
        to="/systems/$id"
        params={{ id: system.id }}
        className={['font-medium text-primary hover:underline', className ?? ''].join(' ')}
        {...linkProps}
      >
        {primary}
      </Link>
      {secondary}
    </>
  );
}
